import * as Types from '../../tenants/kwg/generated';

import { GroupFragmentDoc, GroupDetailFragmentDoc } from './groups.fragment.g';
import { RoleAssignmentFragmentDoc } from '../roleAssigments/roleAssignments.fragment.g';
import { useQuery, useSuspenseQuery, useInfiniteQuery, useSuspenseInfiniteQuery, UseQueryOptions, UseSuspenseQueryOptions, UseInfiniteQueryOptions, InfiniteData, UseSuspenseInfiniteQueryOptions } from '@tanstack/react-query';
import { fetcher } from '../fetcher';


export const GroupsDocument = `
    query Groups($order: [GroupSortInput!], $first: Int, $after: String, $where: GroupFilterInput) {
  groups(order: $order, first: $first, after: $after, where: $where) {
    edges {
      node {
        ...Group
      }
    }
    totalCount
    pageInfo {
      startCursor
      endCursor
      hasNextPage
      hasPreviousPage
    }
  }
}
    ${GroupFragmentDoc}
${RoleAssignmentFragmentDoc}`;

export const useGroupsQuery = <
      TData = Types.GroupsQuery,
      TError = unknown
    >(
      variables?: Types.GroupsQueryVariables,
      options?: Omit<UseQueryOptions<Types.GroupsQuery, TError, TData>, 'queryKey'> & { queryKey?: UseQueryOptions<Types.GroupsQuery, TError, TData>['queryKey'] }
    ) => {
    
    return useQuery<Types.GroupsQuery, TError, TData>(
      {
    queryKey: variables === undefined ? ['Groups'] : ['Groups', variables],
    queryFn: fetcher<Types.GroupsQuery, Types.GroupsQueryVariables>(GroupsDocument, variables),
    ...options
  }
    )};

export const useSuspenseGroupsQuery = <
      TData = Types.GroupsQuery,
      TError = unknown
    >(
      variables?: Types.GroupsQueryVariables,
      options?: Omit<UseSuspenseQueryOptions<Types.GroupsQuery, TError, TData>, 'queryKey'> & { queryKey?: UseSuspenseQueryOptions<Types.GroupsQuery, TError, TData>['queryKey'] }
    ) => {
    
    return useSuspenseQuery<Types.GroupsQuery, TError, TData>(
      {
    queryKey: variables === undefined ? ['Groups'] : ['Groups', variables],
    queryFn: fetcher<Types.GroupsQuery, Types.GroupsQueryVariables>(GroupsDocument, variables),
    ...options
  }
    )};

export const useInfiniteGroupsQuery = <
      TData = InfiniteData<Types.GroupsQuery>,
      TError = unknown
    >(
      variables: Types.GroupsQueryVariables,
      options: Omit<UseInfiniteQueryOptions<Types.GroupsQuery, TError, TData>, 'queryKey'> & { queryKey?: UseInfiniteQueryOptions<Types.GroupsQuery, TError, TData>['queryKey'] }
    ) => {
    
    return useInfiniteQuery<Types.GroupsQuery, TError, TData>(
      (() => {
    const { queryKey: optionsQueryKey, ...restOptions } = options;
    return {
      queryKey: optionsQueryKey ?? variables === undefined ? ['Groups.infinite'] : ['Groups.infinite', variables],
      queryFn: (metaData) => fetcher<Types.GroupsQuery, Types.GroupsQueryVariables>(GroupsDocument, {...variables, ...(metaData.pageParam ?? {})})(),
      ...restOptions
    }
  })()
    )};

export const useSuspenseInfiniteGroupsQuery = <
      TData = InfiniteData<Types.GroupsQuery>,
      TError = unknown
    >(
      variables: Types.GroupsQueryVariables,
      options: Omit<UseSuspenseInfiniteQueryOptions<Types.GroupsQuery, TError, TData>, 'queryKey'> & { queryKey?: UseSuspenseInfiniteQueryOptions<Types.GroupsQuery, TError, TData>['queryKey'] }
    ) => {
    
    return useSuspenseInfiniteQuery<Types.GroupsQuery, TError, TData>(
      (() => {
    const { queryKey: optionsQueryKey, ...restOptions } = options;
    return {
      queryKey: optionsQueryKey ?? variables === undefined ? ['Groups.infinite'] : ['Groups.infinite', variables],
      queryFn: (metaData) => fetcher<Types.GroupsQuery, Types.GroupsQueryVariables>(GroupsDocument, {...variables, ...(metaData.pageParam ?? {})})(),
      ...restOptions
    }
  })()
    )};

export const GroupByIdDocument = `
    query GroupById($id: UUID!) {
  groupById(id: $id) {
    ...GroupDetail
  }
}
    ${GroupDetailFragmentDoc}
${RoleAssignmentFragmentDoc}`;

export const useGroupByIdQuery = <
      TData = Types.GroupByIdQuery,
      TError = unknown
    >(
      variables: Types.GroupByIdQueryVariables,
      options?: Omit<UseQueryOptions<Types.GroupByIdQuery, TError, TData>, 'queryKey'> & { queryKey?: UseQueryOptions<Types.GroupByIdQuery, TError, TData>['queryKey'] }
    ) => {
    
    return useQuery<Types.GroupByIdQuery, TError, TData>(
      {
    queryKey: ['GroupById', variables],
    queryFn: fetcher<Types.GroupByIdQuery, Types.GroupByIdQueryVariables>(GroupByIdDocument, variables),
    ...options
  }
    )};

export const useSuspenseGroupByIdQuery = <
      TData = Types.GroupByIdQuery,
      TError = unknown
    >(
      variables: Types.GroupByIdQueryVariables,
      options?: Omit<UseSuspenseQueryOptions<Types.GroupByIdQuery, TError, TData>, 'queryKey'> & { queryKey?: UseSuspenseQueryOptions<Types.GroupByIdQuery, TError, TData>['queryKey'] }
    ) => {
    
    return useSuspenseQuery<Types.GroupByIdQuery, TError, TData>(
      {
    queryKey: ['GroupById', variables],
    queryFn: fetcher<Types.GroupByIdQuery, Types.GroupByIdQueryVariables>(GroupByIdDocument, variables),
    ...options
  }
    )};

export const useInfiniteGroupByIdQuery = <
      TData = InfiniteData<Types.GroupByIdQuery>,
      TError = unknown
    >(
      variables: Types.GroupByIdQueryVariables,
      options: Omit<UseInfiniteQueryOptions<Types.GroupByIdQuery, TError, TData>, 'queryKey'> & { queryKey?: UseInfiniteQueryOptions<Types.GroupByIdQuery, TError, TData>['queryKey'] }
    ) => {
    
    return useInfiniteQuery<Types.GroupByIdQuery, TError, TData>(
      (() => {
    const { queryKey: optionsQueryKey, ...restOptions } = options;
    return {
      queryKey: optionsQueryKey ?? ['GroupById.infinite', variables],
      queryFn: (metaData) => fetcher<Types.GroupByIdQuery, Types.GroupByIdQueryVariables>(GroupByIdDocument, {...variables, ...(metaData.pageParam ?? {})})(),
      ...restOptions
    }
  })()
    )};

export const useSuspenseInfiniteGroupByIdQuery = <
      TData = InfiniteData<Types.GroupByIdQuery>,
      TError = unknown
    >(
      variables: Types.GroupByIdQueryVariables,
      options: Omit<UseSuspenseInfiniteQueryOptions<Types.GroupByIdQuery, TError, TData>, 'queryKey'> & { queryKey?: UseSuspenseInfiniteQueryOptions<Types.GroupByIdQuery, TError, TData>['queryKey'] }
    ) => {
    
    return useSuspenseInfiniteQuery<Types.GroupByIdQuery, TError, TData>(
      (() => {
    const { queryKey: optionsQueryKey, ...restOptions } = options;
    return {
      queryKey: optionsQueryKey ?? ['GroupById.infinite', variables],
      queryFn: (metaData) => fetcher<Types.GroupByIdQuery, Types.GroupByIdQueryVariables>(GroupByIdDocument, {...variables, ...(metaData.pageParam ?? {})})(),
      ...restOptions
    }
  })()
    )};
